import { ApiFailure } from "@/components/data-display/api-failure";
import type { ApiErrorInfo } from "@/lib/api";
import { Skeleton } from "@/components/ui/feedback";
import { useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { Wallet, WalletCards, Clock, ArrowRightLeft } from "lucide-react";
import { useWallets } from "@/hooks/use-wallets";
import { useTransfers } from "@/hooks/use-transfers";
import { useApprovals } from "@/hooks/use-approvals";
import { useNotifications } from "@/hooks/use-notifications";
import { useValuationSummary } from "@/features/valuation/use-valuation-summary";
import { useLoadedPermission } from "@/hooks/use-permission";
import { displayTotal, formatUsdValue } from "@/features/valuation/valuation";
import {
  getTotalAum,
  getAumKpiSub,
  getActiveWalletCount,
  getWalletChainCounts,
  getAumByWalletType,
  getChainDistributionGrouped,
  getTopChainByAum,
  getPendingApprovals,
  getAttentionItems,
} from "./dashboard-selectors";
import {
  DASHBOARD_CHAIN_OTHERS_THRESHOLD,
  DASHBOARD_RECENT_TRANSFER_LIMIT,
  DASHBOARD_APPROVAL_QUEUE_LIMIT,
  DASHBOARD_NOTIFICATION_LIMIT,
} from "./dashboard-data";
import { HeroSection, type HeroKpi } from "./components/hero";
import { AttentionBar } from "./components/attention-bar";
import { AumComposition } from "./components/aum-composition";
import { ChainDistribution } from "./components/chain-distribution";
import { RecentTransfers } from "./components/recent-transfers";
import { PendingApprovals } from "./components/pending-approvals";
import { NotificationsPanel } from "./components/notifications-panel";
import { ProviderChainHealth } from "./components/provider-chain-health";
import { SystemStatus } from "./components/system-status";
import { ReconRuns } from "./components/recon-runs";
import { PolicyOutcomes } from "./components/policy-outcomes";
import { OperationalInsight } from "./components/operational-insight";

// The whole failure is passed through, not just its reason code: ApiFailure
// needs the server support reference (and any 403 capability) that a bare
// reason-code string would drop, so SupportDetails can render beside the error.
function ErrorTile({ title, error, onRetry }: { title: string; error: ApiErrorInfo | string; onRetry: () => void }) {
  return (
    <div className="flex h-full flex-col">
      <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">{title}</h2>
      <ApiFailure error={error} subject={title.toLowerCase()} onRetry={onRetry} />
    </div>
  );
}

function LoadingTile({ title }: { title: string }) {
  return (
    <div className="flex h-full flex-col">
      <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">{title}</h2>
      <div role="status" aria-label={`Loading ${title}`} className="flex flex-1 flex-col gap-2">
        <Skeleton shimmer className="h-10 w-full" />
        <Skeleton shimmer className="h-10 w-full" />
        <Skeleton shimmer className="h-10 w-3/4" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const approvalAccess = useLoadedPermission("approval:read", "approvals:read");
  const { wallets, loading: walletsLoading, error: walletsError, refresh: refreshWallets } = useWallets();
  const { transfers, loading: transfersLoading, error: transfersError, refresh: refreshTransfers } = useTransfers();
  const { mine: pendingApprovalsRaw, loading: approvalsLoading, error: approvalsError, refresh: refreshApprovals } = useApprovals(approvalAccess.allowed);
  const { notifications, unreadCount, loading: notificationsLoading, error: notificationsError, refresh: refreshNotifications, markRead } = useNotifications();
  const { summary: gatewayValuationSummary } = useValuationSummary(!walletsLoading && !walletsError);

  const localAum = useMemo(() => getTotalAum(wallets), [wallets]);
  const totalAum = gatewayValuationSummary ?? localAum;
  // Handed `totalAum` so the type rows decompose the *same* AUM figure the
  // hero shows; summing the wallets alone would total the reference-priced
  // balances too and disagree with the headline (see the selector).
  const aumByType = useMemo(() => getAumByWalletType(wallets, totalAum), [wallets, totalAum]);
  const chainDistribution = useMemo(() => getChainDistributionGrouped(wallets, DASHBOARD_CHAIN_OTHERS_THRESHOLD), [wallets]);
  const { walletCount } = useMemo(() => getWalletChainCounts(wallets), [wallets]);
  const activeWalletCount = useMemo(() => getActiveWalletCount(wallets), [wallets]);
  // Largest chain by AUM, straight off the wallets this page already fetched.
  // null while wallets are loading, errored or empty; OperationalInsight then
  // simply omits that clause instead of guessing.
  const topChain = useMemo(() => getTopChainByAum(wallets), [wallets]);
  // Full (unsliced) list — its .length feeds the Hero KPI and attention-bar
  // counts, which must reflect the true total, never the display-limited
  // page-size (undercounting "5" when 12 are actually pending would be its
  // own kind of fabrication).
  const pendingApprovals = useMemo(() => getPendingApprovals(pendingApprovalsRaw), [pendingApprovalsRaw]);
  const pendingApprovalsDisplay = useMemo(
    () => pendingApprovals.slice(0, DASHBOARD_APPROVAL_QUEUE_LIMIT),
    [pendingApprovals],
  );
  const recentTransfers = useMemo(() => transfers.slice(0, DASHBOARD_RECENT_TRANSFER_LIMIT), [transfers]);
  const recentNotifications = useMemo(() => notifications.slice(0, DASHBOARD_NOTIFICATION_LIMIT), [notifications]);

  const attentionHasError = Boolean(notificationsError) || (approvalAccess.allowed && Boolean(approvalsError));
  const attentionItems = useMemo(
    () =>
      getAttentionItems(
        approvalAccess.allowed && !approvalsError ? pendingApprovals.length : null,
        notificationsError ? null : unreadCount,
      ),
    [approvalAccess.allowed, approvalsError, pendingApprovals.length, notificationsError, unreadCount],
  );

  const kpis: HeroKpi[] = [
    {
      key: "aum",
      icon: Wallet,
      label: "Total AUM",
      value: formatUsdValue(displayTotal(totalAum).value),
      // The composition's caption repeats this sentence from the same helper
      // and the same summary field, so both AUM figures on the page state the
      // same exclusions.
      sub: getAumKpiSub(totalAum),
      href: "/wallets",
      loading: walletsLoading,
      error: walletsError ? walletsError.reasonCode ?? "error" : null,
    },
    {
      key: "wallets",
      icon: WalletCards,
      label: "Active Wallets",
      value: String(activeWalletCount),
      sub: `of ${walletCount} provisioned`,
      href: "/wallets",
      loading: walletsLoading,
      error: walletsError ? walletsError.reasonCode ?? "error" : null,
    },
    {
      key: "transfers",
      icon: ArrowRightLeft,
      label: "Transfers",
      value: String(transfers.length),
      sub: "in your recent transfer history",
      href: "/transfers",
      loading: transfersLoading,
      error: transfersError,
    },
  ];
  if (approvalAccess.allowed) {
    kpis.splice(2, 0, {
      key: "approvals",
      icon: Clock,
      label: "Pending Approvals",
      value: String(pendingApprovals.length),
      sub: pendingApprovals.length > 0 ? "require action" : "all caught up",
      href: "/approvals",
      loading: approvalsLoading,
      error: approvalsError,
    });
  }

  return (
    <div className="space-y-4 pb-8">
      <HeroSection kpis={kpis} />
      <AttentionBar items={attentionItems} hasError={attentionHasError} includeApprovals={approvalAccess.allowed} />

      {/* Primary analytical area */}
      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[1fr_360px]">
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          {walletsLoading ? (
            <LoadingTile title="AUM Composition" />
          ) : walletsError ? (
            <ErrorTile title="AUM Composition" error={walletsError} onRetry={refreshWallets} />
          ) : (
            <AumComposition items={aumByType} total={totalAum} wallets={wallets} />
          )}
        </section>
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          {walletsLoading ? (
            <LoadingTile title="Assets by Chain" />
          ) : walletsError ? (
            <ErrorTile title="Assets by Chain" error={walletsError} onRetry={refreshWallets} />
          ) : (
            <ChainDistribution items={chainDistribution} total={totalAum} wallets={wallets} />
          )}
        </section>
      </div>

      {/* Infrastructure health. Both tiles are honest not-available states:
          this app serves no monitoring / provider / chain endpoint, and the
          fixtures behind the prototypes would be fabricated telemetry. */}
      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <ProviderChainHealth />
        </section>
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <SystemStatus />
        </section>
      </div>

      {/* Controls: reconciliation + policy. Same rule — no reconciliation run
          or policy-evaluation endpoint exists, so both render not-available. */}
      <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <ReconRuns />
        </section>
        <section className="flex h-full flex-col rounded-2xl border border-line bg-surface p-5 md:p-6">
          <PolicyOutcomes />
        </section>
      </div>

      {/* Activity + approvals + notifications */}
      <div className={`grid grid-cols-1 items-start gap-4 ${approvalAccess.allowed ? "xl:grid-cols-[1fr_360px_360px]" : "xl:grid-cols-[1fr_360px]"}`}>
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          {transfersLoading ? (
            <LoadingTile title="Recent operations" />
          ) : transfersError ? (
            <ErrorTile title="Recent operations" error={transfersError} onRetry={refreshTransfers} />
          ) : (
            <RecentTransfers transfers={recentTransfers} />
          )}
        </section>
        {approvalAccess.allowed && <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          {approvalsLoading ? (
            <LoadingTile title="Pending approvals" />
          ) : approvalsError ? (
            <ErrorTile title="Pending approvals" error={approvalsError} onRetry={refreshApprovals} />
          ) : (
            <PendingApprovals approvals={pendingApprovalsDisplay} allApprovals={pendingApprovals} />
          )}
        </section>}
        <section className="rounded-2xl border border-line bg-surface p-5 md:p-6">
          <NotificationsPanel
            notifications={recentNotifications}
            loading={notificationsLoading}
            error={notificationsError}
            onMarkRead={markRead}
            onRetry={refreshNotifications}
          />
        </section>
      </div>

      {/* Same hasError gate as the attention bar: a failed source is unknown,
          not healthy. Alert / degraded-provider counts are deliberately not
          passed — they have no source in this app. */}
      <OperationalInsight
        attentionItems={attentionItems}
        pendingCount={approvalAccess.allowed && !approvalsError ? pendingApprovals.length : 0}
        topChain={topChain}
        hasError={attentionHasError}
      />
    </div>
  );
}
